import { daysBetweenInclusive } from '@mm/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { Card, EmptyState, Spinner } from '../../components/ui/display';
import type { PnlSeries } from '../../types/models';
import { formatDate } from '../../utils/format';
import { PnlChart, PnlSummary, toPoint } from './pnlParts';

/** Day buckets for short ranges, months up to ~3 years, years beyond. */
export const granularityFor = (start: string, end: string) => {
  const days = daysBetweenInclusive(start, end);
  return days <= 92 ? 'day' : days <= 1100 ? 'month' : 'year';
};

/**
 * Revenue, expenses and operating profit over time for a range (optionally one
 * app), with the headline figures after month-end actuals.
 */
export function PerformanceCard({
  startDate,
  endDate,
  appId,
  title = 'Performance',
  showSummary = true,
}: {
  startDate: string;
  endDate: string;
  appId?: string;
  title?: string;
  showSummary?: boolean;
}) {
  const granularity = granularityFor(startDate, endDate);
  const query = useQuery({
    queryKey: ['reports', 'pnl', granularity, startDate, endDate, 'CALENDAR', appId ?? ''],
    queryFn: () => api.get<PnlSeries>('/reports/pnl', { startDate, endDate, granularity, appId }),
    placeholderData: keepPreviousData,
  });
  const d = query.data;
  const empty =
    d &&
    d.buckets.every(
      (b) => Number(b.estimated.revenue) === 0 && Number(b.estimated.operatingExpense) === 0,
    );
  const report =
    granularity === 'day'
      ? '/reports/daily'
      : granularity === 'month'
        ? '/reports/monthly'
        : '/reports/yearly';

  return (
    <>
      {showSummary && d && (
        <PnlSummary f={d.totals.actual} revenueForeign={d.totals.revenueForeign} />
      )}
      <Card
        title={title}
        description={`${formatDate(startDate)} – ${formatDate(endDate)} · ${granularity === 'day' ? 'daily' : granularity === 'month' ? 'monthly' : 'yearly'} revenue, operating expense and profit`}
        actions={
          <Link
            to={`${report}?preset=custom&start=${startDate}&end=${endDate}${appId ? `&appId=${appId}` : ''}`}
            className="text-sm text-brand-700 hover:underline"
          >
            Full report
          </Link>
        }
      >
        {query.isLoading ? (
          <Spinner />
        ) : empty ? (
          <EmptyState title="No data in this period" />
        ) : d ? (
          <PnlChart
            height={260}
            mode={granularity === 'day' ? 'ads' : 'full'}
            data={d.buckets.map((b) =>
              toPoint(
                granularity === 'day' ? formatDate(b.key).slice(0, 6) : b.label,
                b.actual,
                granularity === 'day' ? 'ads' : 'full',
              ),
            )}
          />
        ) : null}
      </Card>
    </>
  );
}
